import { forwardRef, useImperativeHandle } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { getLocalizedDateTime, getLocalizedName } from '../../localization/LocalizedString';

const getShipmentId = (order) => {
  const { devices } = order;
  return devices?.[0]?.shipmentId || '';
};

const getCreatedAt = (order, language) => {
  return getLocalizedDateTime(new Date(order.createdAt), language);
};

const getContractorName = (order, language) => {
  return getLocalizedName(order?.orderData?.subcontractor, language);
};

const getCustomerInfo = (order, field) => {
  return order?.customer?.[field] || '';
};

const getConsignorName = (order, field) => {
  return order?.orderData?.[field] || '';
};

const getAgreementDate = (order, translate) => {
  const timeAgreementDates = order?.status?.timeAgreementDates;
  if (Array.isArray(timeAgreementDates) && timeAgreementDates.length > 0) {
    const { startDate, endDate } = timeAgreementDates[timeAgreementDates.length - 1] || {};
    if (startDate && endDate) {
      const dateFmt = translate('work_list.column_task.agreement-date-format');
      const timeFmt = translate('work_list.column_task.agreement-time-format');
      return `${moment(startDate).format(dateFmt)}-${moment(endDate).format(timeFmt)}`;
    }
  }
  return '';
};

const getStatus = (order, translate) => {
  return translate(`work_list.status.${order?.status?.value}`);
};

const getDevicesNames = (order, language) => {
  return (order?.devices || []).map((d) => getLocalizedName(d, language)).join(', ');
};

const getProductNames = (order, language) => {
  return (order?.orderData?.deviceAdditionalProducts || [])
    .map((p) => getLocalizedName(p, language))
    .join(', ');
};

const ExcelResult = forwardRef(({ data, translate, language }, ref) => {
  const download = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(translate('navigation.worklist'));

    worksheet.addRow([
      translate('work_list.column_task.shipmentId'),
      translate('work_list.column_task.createdAt'),
      translate('work_list.column_task.subcontractorName'),
      translate('work_list.column_task.customerName'),
      translate('work_list.column_task.consignorName'),
      translate('work_list.column_task.customerStreetAddress'),
      translate('work_list.column_task.customerPostalCode'),
      translate('work_list.column_task.devicesName'),
      translate('work_list.column_task.deviceAdditionalProductName'),
      translate('work_list.column_task.customerCity'),
      translate('work_list.column_task.customerPhone'),
      translate('work_list.column_task.date'),
      translate('work_list.column_task.status'),
      translate('service_installation.installation_parking_info'),
    ]);

    data.forEach((order) => {
      worksheet.addRow([
        getShipmentId(order),
        getCreatedAt(order, language),
        getContractorName(order, language),
        getCustomerInfo(order, 'name'),
        getConsignorName(order, 'consignorName'),
        getCustomerInfo(order, 'streetAddress'),
        getCustomerInfo(order, 'postalCode'),
        getDevicesNames(order, language),
        getProductNames(order, language),
        getCustomerInfo(order, 'city'),
        getCustomerInfo(order, 'phone'),
        getAgreementDate(order, translate),
        getStatus(order, translate),
        getCustomerInfo(order, 'parkingInfo'),
      ]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const title = language === 'fi' ? 'Työlista' : 'Work list';
    saveAs(new Blob([buffer]), `${title}.xlsx`);
  };

  useImperativeHandle(ref, () => ({
    download,
  }));

  return null;
});

export default ExcelResult;
